/**
 * Rename an output template. A thin PATCH dialog mirroring the library's other
 * single-field dialogs.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { getApiErrorMessage, type IApiAxiosError } from "@/api";
import type { OutputTemplateListItem } from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useRenameTemplate } from "./useOutputTemplates";

interface RenameTemplateDialogProps {
    open: boolean;
    templateId: number;
    currentName: string;
    onClose: () => void;
    onRenamed?: (template: OutputTemplateListItem) => void;
}

export function RenameTemplateDialog({
    open,
    templateId,
    currentName,
    onClose,
    onRenamed,
}: RenameTemplateDialogProps) {
    const [name, setName] = useState(currentName);
    const [error, setError] = useState<string | null>(null);
    const rename = useRenameTemplate();

    useEffect(() => {
        if (open) {
            setName(currentName);
            setError(null);
        }
    }, [open, currentName]);

    const trimmed = name.trim();
    const canSubmit =
        trimmed.length > 0 && trimmed !== currentName && !rename.isPending;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!trimmed) return;
        setError(null);
        rename.mutate(
            { id: templateId, name: trimmed },
            {
                onSuccess: (template) => {
                    onRenamed?.(template);
                    onClose();
                },
                onError: (err) => {
                    setError(
                        getApiErrorMessage(
                            err as IApiAxiosError,
                            "Rename failed"
                        )
                    );
                },
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Rename template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="template-rename-input">Name</Label>
                        <Input
                            id="template-rename-input"
                            data-testid="template-rename-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {error && (
                        <p className="text-destructive text-sm">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={rename.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="template-rename-submit"
                            disabled={!canSubmit}
                        >
                            {rename.isPending && (
                                <Loader2
                                    className="mr-2 size-4 animate-spin"
                                    aria-hidden
                                />
                            )}
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
